/**
 * Collection: Section Rendering API. Replaces [data-collection-products-mount] and
 * [data-collection-filters-mount] when both exist; otherwise replaces the whole section.
 */

const FORM_SELECTOR = '[data-collection-filter-form]';
const PRODUCTS_MOUNT = '[data-collection-products-mount]';
const FILTERS_MOUNT = '[data-collection-filters-mount]';
const PRODUCT_GRID_SELECTOR = `${PRODUCTS_MOUNT} ul[data-card-page-size]`;
const PRODUCT_CARD_SELECTOR = 'li[data-product-id]';
const FETCH_INIT = { credentials: 'same-origin' };

let checkboxDebounce;
const cardPageCache = new Map();

function domId(sectionId) {
  return `shopify-section-${sectionId}`;
}

function parseSection(html, sectionId) {
  return new DOMParser().parseFromString(html, 'text/html').getElementById(domId(sectionId));
}

function htmlFromSectionsJson(data, sectionId) {
  const direct = data[sectionId];
  if (typeof direct === 'string' && direct) return direct;
  const keys = Object.keys(data).filter((k) => typeof data[k] === 'string' && data[k]);
  return keys.length === 1 ? data[keys[0]] : null;
}

async function requestSectionElement(viewUrl, sectionId) {
  const jsonUrl = new URL(viewUrl);
  jsonUrl.searchParams.set('sections', sectionId);
  const jsonRes = await fetch(jsonUrl, { ...FETCH_INIT, headers: { Accept: 'application/json' } });

  if (jsonRes.ok) {
    try {
      const data = await jsonRes.json();
      const html = htmlFromSectionsJson(data, sectionId);
      if (html) {
        const el = parseSection(html, sectionId);
        if (el) return el;
      }
    } catch {
      /* fall through */
    }
  }

  const htmlUrl = new URL(viewUrl);
  htmlUrl.searchParams.set('section_id', sectionId);
  const htmlRes = await fetch(htmlUrl, { ...FETCH_INIT, headers: { Accept: 'text/html' } });
  if (!htmlRes.ok) throw new Error(`HTTP ${htmlRes.status}`);

  const el = parseSection(await htmlRes.text(), sectionId);
  if (!el) throw new Error('Section not in response');
  return el;
}

function viewUrlFromForm(form) {
  const sectionId = form.getAttribute('data-section-id');
  if (!sectionId) throw new Error('Missing data-section-id');

  const viewUrl = new URL(window.location.pathname, window.location.origin);
  viewUrl.search = '';
  new URLSearchParams(new FormData(form)).forEach((v, k) => viewUrl.searchParams.append(k, v));
  return { sectionId, viewUrl };
}

function getPageNumber(url = window.location.href) {
  const page = Number(new URL(url, window.location.origin).searchParams.get('page') || '1');
  return Number.isFinite(page) && page > 0 ? page : 1;
}

function getPaginationPageCount(root) {
  const nav = root.querySelector(`${PRODUCTS_MOUNT} nav`);
  if (!nav) return 1;

  const pageNumbers = [...nav.querySelectorAll('a, span')]
    .map((el) => Number(el.textContent.trim()))
    .filter((page) => Number.isFinite(page));

  return Math.max(1, ...pageNumbers);
}

function getCardPageCacheKey(viewUrl, sectionId) {
  const keyUrl = new URL(viewUrl, window.location.origin);
  keyUrl.searchParams.delete('page');
  keyUrl.searchParams.delete('sections');
  keyUrl.searchParams.delete('section_id');
  return `${sectionId}:${keyUrl.pathname}?${keyUrl.searchParams.toString()}`;
}

function cloneProductCards(root) {
  return [...root.querySelectorAll(`${PRODUCTS_MOUNT} ${PRODUCT_CARD_SELECTOR}`)].map((card) =>
    card.cloneNode(true)
  );
}

function pageUrl(viewUrl, page) {
  const url = new URL(viewUrl, window.location.origin);
  url.searchParams.delete('sections');
  url.searchParams.delete('section_id');
  url.searchParams.set('page', String(page));
  return url;
}

async function getAllProductCards(viewUrl, sectionId, currentRoot) {
  const cacheKey = getCardPageCacheKey(viewUrl, sectionId);
  const cached = cardPageCache.get(cacheKey);
  if (cached) return cached;

  const currentPage = getPageNumber(viewUrl);
  const pageCount = getPaginationPageCount(currentRoot);
  const cards = [];

  for (let page = 1; page <= pageCount; page += 1) {
    const pageRoot =
      page === currentPage ? currentRoot : await requestSectionElement(pageUrl(viewUrl, page), sectionId);
    cards.push(...cloneProductCards(pageRoot));
  }

  cardPageCache.set(cacheKey, cards);
  return cards;
}

function translatedPaginationLabels(nav) {
  const isGerman = document.documentElement.lang?.toLowerCase().startsWith('de');
  const labels = [...nav.querySelectorAll('a')]
    .map((link) => link.textContent.trim())
    .filter((label) => label && !Number.isFinite(Number(label)));

  return {
    previous: labels.find((label) => /previous|zurück/i.test(label)) || (isGerman ? 'Zurück' : 'Previous'),
    next: labels.find((label) => /next|weiter/i.test(label)) || (isGerman ? 'Weiter' : 'Next'),
  };
}

function buildPageLink(page, currentPage, viewUrl) {
  if (page === currentPage) {
    const current = document.createElement('span');
    current.className = 'flex items-center justify-center w-[44px] h-[44px] border bg-[#212121] text-white';
    current.setAttribute('aria-current', 'page');
    current.textContent = String(page);
    return current;
  }

  const link = document.createElement('a');
  link.href = pageUrl(viewUrl, page).pathname + pageUrl(viewUrl, page).search;
  link.dataset.collectionPagination = '';
  link.className = 'flex items-center justify-center w-[44px] h-[44px] border-none';
  link.textContent = String(page);
  return link;
}

function replacePagination(mount, currentPage, totalPages, viewUrl) {
  const currentNav = mount.querySelector('nav');
  if (!currentNav) return;

  if (totalPages <= 1) {
    currentNav.remove();
    return;
  }

  const labels = translatedPaginationLabels(currentNav);
  const nav = document.createElement('nav');
  nav.className = currentNav.className;
  nav.setAttribute('aria-label', currentNav.getAttribute('aria-label') || 'Pagination');

  if (currentPage > 1) {
    const previous = document.createElement('a');
    previous.href = pageUrl(viewUrl, currentPage - 1).pathname + pageUrl(viewUrl, currentPage - 1).search;
    previous.dataset.collectionPagination = '';
    previous.className = 'btn --primary --outline w-22';
    previous.textContent = labels.previous;
    nav.append(previous);
  }

  const list = document.createElement('ol');
  list.className = 'flex flex-wrap items-center justify-center';
  list.setAttribute('role', 'list');

  for (let page = 1; page <= totalPages; page += 1) {
    const item = document.createElement('li');
    item.append(buildPageLink(page, currentPage, viewUrl));
    list.append(item);
  }

  nav.append(list);

  if (currentPage < totalPages) {
    const next = document.createElement('a');
    next.href = pageUrl(viewUrl, currentPage + 1).pathname + pageUrl(viewUrl, currentPage + 1).search;
    next.dataset.collectionPagination = '';
    next.className = 'btn --primary --outline w-22';
    next.textContent = labels.next;
    nav.append(next);
  }

  currentNav.replaceWith(nav);
}

async function normalizeProductCardPagination(viewUrl, sectionId, root) {
  const grid = root.querySelector(PRODUCT_GRID_SELECTOR);
  const mount = root.querySelector(PRODUCTS_MOUNT);
  if (!(grid instanceof HTMLElement) || !(mount instanceof HTMLElement)) return;

  const pageSize = Number(grid.dataset.cardPageSize || '36');
  if (!Number.isFinite(pageSize) || pageSize <= 0) return;

  const cards = await getAllProductCards(viewUrl, sectionId, root);
  const totalPages = Math.max(1, Math.ceil(cards.length / pageSize));
  const currentPage = Math.min(getPageNumber(viewUrl), totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageCards = cards.slice(start, start + pageSize).map((card) => card.cloneNode(true));

  grid.replaceChildren(...pageCards);
  replacePagination(mount, currentPage, totalPages, viewUrl);
}

async function updateCollectionSection(viewUrl, sectionId, pushHistory) {
  const nextRoot = await requestSectionElement(viewUrl, sectionId);
  const liveRoot = document.getElementById(domId(sectionId));
  if (!liveRoot) throw new Error('Section not in DOM');

  const liveProducts = liveRoot.querySelector(PRODUCTS_MOUNT);
  const nextProducts = nextRoot.querySelector(PRODUCTS_MOUNT);
  const liveFilters = liveRoot.querySelector(FILTERS_MOUNT);
  const nextFilters = nextRoot.querySelector(FILTERS_MOUNT);

  const partial =
    liveProducts instanceof HTMLElement &&
    nextProducts instanceof HTMLElement &&
    liveFilters instanceof HTMLElement &&
    nextFilters instanceof HTMLElement;

  if (partial) {
    liveProducts.replaceWith(document.importNode(nextProducts, true));
    liveFilters.replaceWith(document.importNode(nextFilters, true));
  } else {
    liveRoot.replaceWith(document.importNode(nextRoot, true));
  }

  const updatedRoot = document.getElementById(domId(sectionId));
  if (updatedRoot) {
    await normalizeProductCardPagination(viewUrl, sectionId, updatedRoot);
  }

  if (pushHistory) {
    const bar = new URL(viewUrl);
    bar.searchParams.delete('sections');
    bar.searchParams.delete('section_id');
    history.pushState({}, '', bar.pathname + bar.search);
  }
}

async function applyForm(form) {
  if (window.Shopify?.designMode) {
    form.submit();
    return;
  }
  try {
    const { sectionId, viewUrl } = viewUrlFromForm(form);
    await updateCollectionSection(viewUrl, sectionId, true);
  } catch {
    form.submit();
  }
}

function init() {
  const initialForm = document.querySelector(FORM_SELECTOR);
  const initialSectionId = initialForm?.getAttribute('data-section-id');
  const initialRoot = initialSectionId ? document.getElementById(domId(initialSectionId)) : null;
  if (initialSectionId && initialRoot) {
    normalizeProductCardPagination(new URL(window.location.href), initialSectionId, initialRoot).catch(() => {});
  }

  document.body.addEventListener('submit', (e) => {
    const form = e.target.closest(FORM_SELECTOR);
    if (!form) return;
    e.preventDefault();
    applyForm(form);
  });

  document.body.addEventListener('change', (e) => {
    const form = e.target.closest(FORM_SELECTOR);
    if (!form) return;

    const t = e.target;
    if (t instanceof HTMLSelectElement && t.name === 'sort_by') {
      applyForm(form);
      return;
    }
    if (t instanceof HTMLInputElement && t.type === 'checkbox' && t.name) {
      clearTimeout(checkboxDebounce);
      checkboxDebounce = setTimeout(() => applyForm(form), 200);
    }
  });

  document.body.addEventListener('click', (e) => {
    const clear = e.target.closest('[data-collection-clear]');
    if (clear instanceof HTMLAnchorElement) {
      const form = clear.closest(FORM_SELECTOR);
      if (!form) return;
      e.preventDefault();

      const sectionId = form.getAttribute('data-section-id');
      if (!sectionId) return;

      if (window.Shopify?.designMode) {
        window.location.href = clear.href;
        return;
      }

      const url = new URL(clear.href, window.location.origin);
      updateCollectionSection(url, sectionId, true).catch(() => {
        window.location.href = clear.href;
      });
      return;
    }

    const pageLink = e.target.closest('[data-collection-pagination]');
    if (!(pageLink instanceof HTMLAnchorElement)) return;

    const form = document.querySelector(FORM_SELECTOR);
    const sectionId = form?.getAttribute('data-section-id');
    if (!sectionId) return;

    e.preventDefault();

    if (window.Shopify?.designMode) {
      window.location.href = pageLink.href;
      return;
    }

    const url = new URL(pageLink.href, window.location.origin);
    const liveRoot = document.getElementById(domId(sectionId));
    const cacheKey = getCardPageCacheKey(url, sectionId);
    const cachedCards = cardPageCache.get(cacheKey);

    if (liveRoot && cachedCards) {
      e.preventDefault();
      normalizeProductCardPagination(url, sectionId, liveRoot)
        .then(() => {
          history.pushState({}, '', url.pathname + url.search);
          liveRoot.scrollIntoView({ behavior: 'smooth', block: 'start' });
        })
        .catch(() => {
          window.location.href = pageLink.href;
        });
      return;
    }

    updateCollectionSection(url, sectionId, true)
      .then(() => {
        document.getElementById(domId(sectionId))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      })
      .catch(() => {
        window.location.href = pageLink.href;
      });
  });

  window.addEventListener('popstate', () => {
    const form = document.querySelector(FORM_SELECTOR);
    const sectionId = form?.getAttribute('data-section-id');
    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    updateCollectionSection(url, sectionId, false).catch(() => window.location.reload());
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
